import { readFileSync } from "fs";
import { resolve } from "path";
import type { Handler } from "hono";
import {
  CLOUD_DASHBOARD_EXPLICIT_ROUTES,
  CLOUD_DASHBOARD_SYSTEM_PATH_PREFIXES,
  createCloudDashboardHandler,
  registerCloudDashboardRoutes,
  shouldServeCloudDashboardSpaFallback,
} from "./server-cloud-dashboard-static.js";
import type { RuntimeOperationInventory } from "./runtime-operation-inventory.js";

type LoggerLike = Pick<Console, "log" | "error">;

type EmbeddedDashboardRouteContext = {
  html(body: string): unknown;
  notFound(): unknown;
};

type EmbeddedDashboardRegistrar = {
  use(path: string, middleware: unknown): void;
  get(path: string, handler: (context: EmbeddedDashboardRouteContext) => Promise<unknown> | unknown): void;
};

type SpaRegistrar = {
  use?(path: string, middleware: unknown): void;
  get(path: string, handler: Handler): void;
};

type ServeStaticFactory = (options: { root: string; rewriteRequestPath(path: string): string }) => unknown;

type BunFileLike = {
  exists(): Promise<boolean>;
  text(): Promise<string>;
};

type WebSocketTimerMap = Pick<WeakMap<object, number>, "get" | "set" | "delete">;

type RuntimeBufferLike = {
  add(name: string, value: number): void;
};

type DashboardWebSocketLike = object;

type DashboardWebSocketEvent = {
  data: unknown;
};

type ReadinessContext = {
  json(body: unknown, status?: number): unknown;
};

type ReadinessSql = (query: string) => Promise<unknown>;

function collectLegacyOnlyReadinessInventory(params: {
  getRegisteredQueries(): unknown[];
  getRegisteredMutations(): unknown[];
}): RuntimeOperationInventory {
  const legacyQueries = params.getRegisteredQueries();
  const legacyMutations = params.getRegisteredMutations();
  return {
    legacyQueries: legacyQueries.map((query, index) => ({
      key: typeof query === "string" ? query : String(index),
      allowAnonymous: false,
    })),
    legacyMutations: legacyMutations.map((mutation, index) => ({
      key: typeof mutation === "string" ? mutation : String(index),
      allowAnonymous: false,
    })),
    procedureQueries: [],
    procedureMutations: [],
    totalQueries: legacyQueries.length,
    totalMutations: legacyMutations.length,
    totalOperations: legacyQueries.length + legacyMutations.length,
  };
}

export const SYSTEM_PATH_PREFIXES = CLOUD_DASHBOARD_SYSTEM_PATH_PREFIXES;
export { CLOUD_DASHBOARD_EXPLICIT_ROUTES };

export function buildEmbeddedDashboardSearchPaths(params: {
  envDashboardDir?: string;
  importMetaDirname: string;
  cwd: string;
  functionsPath?: string;
}): string[] {
  return [
    ...(params.envDashboardDir ? [params.envDashboardDir] : []),
    resolve(params.importMetaDirname, "../../../apps/system/dashboard/dist"),
    resolve(params.cwd, "apps/system/dashboard/dist"),
    resolve(params.importMetaDirname, "../dashboard"),
    resolve(params.importMetaDirname, "dashboard"),
    resolve(params.cwd, "node_modules/gencow/dashboard"),
    ...(params.functionsPath ? [resolve(params.functionsPath, "../node_modules/gencow/dashboard")] : []),
  ];
}

export function buildCloudDashboardSearchPaths(params: { importMetaDirname: string; cwd: string }): string[] {
  return [
    resolve(params.importMetaDirname, "../../../apps/system/cloud-dashboard/dist"),
    resolve(params.cwd, "apps/system/cloud-dashboard/dist"),
    resolve(params.importMetaDirname, "../cloud-dashboard"),
    resolve(params.importMetaDirname, "cloud-dashboard"),
  ];
}

export function findExistingPath(
  paths: string[],
  existsSyncImpl: (path: string) => boolean,
): string | undefined {
  return paths.find((path) => existsSyncImpl(path));
}

export async function registerEmbeddedDashboardRoutes(params: {
  app: EmbeddedDashboardRegistrar;
  isBaas: boolean;
  isPlatform?: boolean;
  envDashboardDir?: string;
  importMetaDirname: string;
  cwd: string;
  functionsPath?: string;
  existsSyncImpl?: (path: string) => boolean;
  bunFileFactory?: (path: string) => BunFileLike;
  serveStaticImpl: ServeStaticFactory;
  logger?: LoggerLike;
}): Promise<boolean> {
  if (params.isBaas || params.isPlatform) {
    return false;
  }

  const existsSyncImpl = params.existsSyncImpl ?? ((path) => false);
  const bunFileFactory = params.bunFileFactory ?? ((path) => Bun.file(path) as BunFileLike);
  const logger = params.logger ?? console;
  const dashboardDir = findExistingPath(
    buildEmbeddedDashboardSearchPaths({
      envDashboardDir: params.envDashboardDir,
      importMetaDirname: params.importMetaDirname,
      cwd: params.cwd,
      functionsPath: params.functionsPath,
    }),
    existsSyncImpl,
  );

  if (!dashboardDir) {
    return false;
  }

  logger.log(`[dashboard] Serving embedded dashboard from: ${dashboardDir}`);
  params.app.use(
    "/_dashboard/*",
    params.serveStaticImpl({
      root: dashboardDir,
      rewriteRequestPath: (path) => path.replace(/^\/_dashboard/, ""),
    }),
  );
  params.app.get("/_dashboard/*", async (c) => {
    const file = bunFileFactory(resolve(dashboardDir, "index.html"));
    if (await file.exists()) {
      return c.html(await file.text());
    }
    return c.notFound();
  });

  return true;
}

export function loadCloudDashboardState(params: {
  isPlatform: boolean;
  importMetaDirname: string;
  cwd: string;
  existsSyncImpl?: (path: string) => boolean;
  readFileSyncImpl?: (path: string, encoding: BufferEncoding) => string;
  logger?: LoggerLike;
}): { dir?: string; html?: string } {
  if (!params.isPlatform) {
    return {};
  }

  const existsSyncImpl = params.existsSyncImpl ?? ((path) => false);
  const readFileSyncImpl = params.readFileSyncImpl ?? readFileSync;
  const logger = params.logger ?? console;
  const dir = findExistingPath(
    buildCloudDashboardSearchPaths({
      importMetaDirname: params.importMetaDirname,
      cwd: params.cwd,
    }),
    existsSyncImpl,
  );

  if (!dir) {
    return {};
  }

  logger.log(`[cloud-dashboard] Serving from: ${dir}`);
  const indexPath = resolve(dir, "index.html");
  const html = existsSyncImpl(indexPath) ? readFileSyncImpl(indexPath, "utf-8") : undefined;
  return { dir, html };
}

export function shouldServeSpaFallback(path: string, prefixes = SYSTEM_PATH_PREFIXES): boolean {
  return shouldServeCloudDashboardSpaFallback(path, prefixes);
}

export function registerCloudDashboardSpa(params: { app: SpaRegistrar; dir?: string; html?: string }): void {
  if (!params.dir || !params.html) {
    return;
  }

  registerCloudDashboardRoutes({
    app: params.app,
    handler: createCloudDashboardHandler({
      cloudDashboardDir: params.dir,
      indexHtml: params.html,
      canServeSpaFallback: shouldServeSpaFallback,
    }),
  });
}

export function createDashboardWebSocketHandlers(params: {
  isBaas: boolean;
  wsTimers: WebSocketTimerMap;
  runtimeBuffer: RuntimeBufferLike;
  registerClient(ws: DashboardWebSocketLike): void;
  subscribeToLogs(ws: DashboardWebSocketLike): void;
  unsubscribeFromLogs(ws: DashboardWebSocketLike): void;
  deregisterClient(ws: DashboardWebSocketLike): void;
  handleWsMessage(ws: DashboardWebSocketLike, message: string): void;
}) {
  return {
    onOpen(_: unknown, ws: DashboardWebSocketLike) {
      params.registerClient(ws);
      if (params.isBaas) {
        params.wsTimers.set(ws, Date.now());
      }
    },
    onMessage(event: DashboardWebSocketEvent, ws: DashboardWebSocketLike) {
      const data = typeof event.data === "string" ? event.data : String(event.data);
      try {
        const message = JSON.parse(data) as { type?: string };
        if (message.type === "log:subscribe") {
          params.subscribeToLogs(ws);
          return;
        }
        if (message.type === "log:unsubscribe") {
          params.unsubscribeFromLogs(ws);
          return;
        }
      } catch {
        /* fall through */
      }
      params.handleWsMessage(ws, data);
    },
    onClose(_: unknown, ws: DashboardWebSocketLike) {
      if (params.isBaas) {
        const openedAt = params.wsTimers.get(ws);
        if (openedAt) {
          params.runtimeBuffer.add("realtime_ms", Date.now() - openedAt);
          params.wsTimers.delete(ws);
        }
      }
      params.unsubscribeFromLogs(ws);
      params.deregisterClient(ws);
    },
  };
}

export function createReadinessHandler(params: {
  rawSql: ReadinessSql;
  getRegisteredQueries(): unknown[];
  getRegisteredMutations(): unknown[];
  getOperationInventory?: () => RuntimeOperationInventory;
  uptimeSeconds?: () => number;
}): (context: ReadinessContext) => Promise<unknown> {
  const uptimeSeconds = params.uptimeSeconds ?? (() => process.uptime());

  return async (c) => {
    try {
      await params.rawSql("SELECT 1 AS ok");
      const inventory = params.getOperationInventory?.() ?? collectLegacyOnlyReadinessInventory(params);
      return c.json({
        status: "ready",
        pid: process.pid,
        db: "ok",
        queries: inventory.totalQueries,
        mutations: inventory.totalMutations,
        legacyQueries: inventory.legacyQueries.length,
        legacyMutations: inventory.legacyMutations.length,
        procedureQueries: inventory.procedureQueries.length,
        procedureMutations: inventory.procedureMutations.length,
        uptime: Math.floor(uptimeSeconds()),
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      return c.json(
        {
          status: "not_ready",
          pid: process.pid,
          error: message,
          uptime: Math.floor(uptimeSeconds()),
        },
        503,
      );
    }
  };
}

export function buildRootStatusPayload(uptimeSeconds: () => number = () => process.uptime()) {
  return {
    status: "running",
    version: "0.1.0",
    uptime: Math.floor(uptimeSeconds()),
  };
}

export function ensureInternalUrl(env: NodeJS.ProcessEnv, port: number): void {
  if (!env.GENCOW_INTERNAL_URL) {
    env.GENCOW_INTERNAL_URL = `http://localhost:${port}`;
  }
}

export function assertBaasPortEnv(params: {
  isBaas: boolean;
  env: NodeJS.ProcessEnv;
  logger?: Pick<Console, "error">;
  exit?: (code: number) => never | void;
}): void {
  if (!params.isBaas || params.env.PORT) {
    return;
  }

  (params.logger ?? console).error(
    "[FATAL] BaaS app started without PORT env var. All apps would collide on :5456. Exiting.",
  );
  (params.exit ?? process.exit)(1);
}

export function buildServerStartupBanner(params: {
  port: number;
  driver: string;
  dashboardEmbedded: boolean;
}): string {
  const apiUrl = `http://localhost:${params.port}`;
  const wsUrl = `ws://localhost:${params.port}/ws`;
  const stackStr = `Bun + Hono + Drizzle + ${params.driver === "postgres" ? "PostgreSQL" : "PGlite"}`;
  const width = 58;
  const pad = (value: string) => "  " + value.padEnd(width);
  const line = "═".repeat(width + 2);
  const dashboardLine = params.dashboardEmbedded
    ? `\n║${pad(`Dashboard: http://localhost:${params.port}/_dashboard`)}║`
    : "";

  return `
╔${line}╗
║${pad("🚀 Gencow API Server Running")}║
║${pad("")}║
║${pad(`API:       ${apiUrl}`)}║
║${pad(`WebSocket: ${wsUrl}`)}║${dashboardLine}
║${pad("")}║
║${pad(`Stack: ${stackStr}`)}║
╚${line}╝
`;
}
